import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Table, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import useFormValidation from 'hooks/useFormValidation';

import SearchableInput from 'components/common/SearchableInput';

import { inventoryApi, shelfApi, productNameApi } from 'api';
import { INVENTORY_TYPE } from 'constants/inventory';

const validationSchema = {
  product: {
    required: 'Product is required',
  },
  inventoryType: {
    required: 'Inventory category is required',
  },
  shelf: {
    required: 'Shelf is required',
  },
  quantity: {
    required: 'Quantity is required',
    type: 'number',
    invalidNumber: 'Please enter a number',
  },
};

const initialValues = {
  product: '',
  productName: '',
  inventoryType: '',
  shelf: '',
  shelfCode: '',
  quantity: '',
};

const INVENTORY_PAGE_SIZE = 20;

const isCanceledError = error =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';

const GatePassItemAddModal = ({ show, onHide, onSave }) => {
  const { selectedWarehouse } = useUserContext();

  // 첫 페이지 로딩 (목록 전체를 숨김) / 추가 페이지 로딩 (하단 스피너만 표시)
  const [isLoadingInventories, setIsLoadingInventories] = useState(false);
  const [isLoadingMoreInventories, setIsLoadingMoreInventories] =
    useState(false);
  // 추가 페이지 로딩 실패 여부. true인 동안은 observer를 만들지 않아 자동 재시도(토스트 반복)를 막는다.
  const [hasLoadMoreError, setHasLoadMoreError] = useState(false);
  const [existingInventories, setExistingInventories] = useState({
    count: 0,
    results: [],
    next: null,
  });
  // 진행 중인 재고 조회 요청. 필터가 바뀌면 이전 요청을 취소해 stale 페이지가 섞이지 않게 한다.
  const inventoryAbortRef = useRef(null);
  const inventoryPageRef = useRef(1);
  const inventoryListRef = useRef(null);
  const inventorySentinelRef = useRef(null);
  const [selectedProductBatch, setSelectedProductBatch] = useState(null);
  const [availableQuantity, setAvailableQuantity] = useState(0);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  } = useFormValidation(initialValues, validationSchema);

  const fetchInventories = useCallback(
    async ({ page, append }) => {
      inventoryAbortRef.current?.abort();
      const controller = new AbortController();
      inventoryAbortRef.current = controller;

      const params = {
        product: Number(values.product),
        page,
        page_size: INVENTORY_PAGE_SIZE,
        warehouse: selectedWarehouse?.id,
        sort: 'expire_date',
      };

      if (values.inventoryType) {
        params.inventory_type = values.inventoryType;
      }

      if (values.shelf) {
        params.shelf = values.shelf;
      }

      setHasLoadMoreError(false);
      if (append) {
        setIsLoadingMoreInventories(true);
      } else {
        setIsLoadingInventories(true);
        setIsLoadingMoreInventories(false);
      }

      try {
        const response = await inventoryApi.getList(params, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        inventoryPageRef.current = page;
        setExistingInventories(prev => ({
          count: response.count,
          next: response.next,
          results: append
            ? [...prev.results, ...response.results]
            : response.results,
        }));
      } catch (error) {
        if (isCanceledError(error) || controller.signal.aborted) return;
        if (append) {
          setHasLoadMoreError(true);
        }
        toast.error('Failed to fetch existing inventories');
      } finally {
        // 취소된 요청은 뒤이어 시작된 요청이 로딩 상태를 관리하므로 건너뛴다.
        if (!controller.signal.aborted) {
          if (append) {
            setIsLoadingMoreInventories(false);
          } else {
            setIsLoadingInventories(false);
          }
        }
      }
    },
    [values.product, values.inventoryType, values.shelf, selectedWarehouse?.id],
  );

  // 필터(product / inventoryType / shelf / warehouse)가 바뀌면 1페이지부터 다시 로드
  useEffect(() => {
    if (!values.product) {
      inventoryAbortRef.current?.abort();
      inventoryPageRef.current = 1;
      setExistingInventories({ count: 0, results: [], next: null });
      setIsLoadingInventories(false);
      setIsLoadingMoreInventories(false);
      return undefined;
    }

    // 첫 페이지 요청이 실패해도 이전 필터의 결과/페이지 상태가 남지 않도록 먼저 비운다.
    inventoryPageRef.current = 1;
    setExistingInventories({ count: 0, results: [], next: null });
    fetchInventories({ page: 1, append: false });

    return () => {
      inventoryAbortRef.current?.abort();
    };
  }, [fetchInventories, values.product]);

  // 목록 하단 sentinel이 스크롤 컨테이너 안에 보이면 다음 페이지를 이어서 로드 (무한스크롤)
  useEffect(() => {
    const sentinel = inventorySentinelRef.current;
    const root = inventoryListRef.current;
    if (!sentinel || !root) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const hasMore = Boolean(existingInventories.next);
    if (
      !hasMore ||
      hasLoadMoreError ||
      isLoadingInventories ||
      isLoadingMoreInventories
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        fetchInventories({ page: inventoryPageRef.current + 1, append: true });
      },
      { root, rootMargin: '0px 0px 40px 0px' },
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    existingInventories.next,
    hasLoadMoreError,
    isLoadingInventories,
    isLoadingMoreInventories,
    fetchInventories,
  ]);

  const handleRetryLoadMore = () => {
    fetchInventories({ page: inventoryPageRef.current + 1, append: true });
  };

  const handleProductInputChange = value => {
    // 입력값이 변경되면 product 초기화
    handleChange('product', '');
    handleChange('productName', value);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleProductSelect = selectedProduct => {
    handleChange('product', selectedProduct.id);
    handleChange('productName', selectedProduct.name);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleShelfInputChange = value => {
    handleChange('shelf', '');
    handleChange('shelfCode', value);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleShelfSelect = selectedShelf => {
    handleChange('shelf', selectedShelf.id);
    handleChange('shelfCode', selectedShelf.code);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleSelectInventory = inventory => {
    handleChange('shelf', inventory.shelf.id);
    handleChange('shelfCode', inventory.shelf.code);
    handleChange('inventoryType', inventory.inventory_type);
    setSelectedProductBatch(inventory?.id);
    setAvailableQuantity(inventory.quantity);
  };

  const handleAddItem = async () => {
    if (!selectedProductBatch) {
      toast.error('Please select an inventory item.');
      return;
    }
    if (Number(values.quantity) > availableQuantity) {
      toast.error('Quantity cannot be greater than available quantity.');
      return;
    }

    const itemData = {
      product_batch: selectedProductBatch,
      quantity: Number(values.quantity),
    };

    onSave(itemData);
    closeModal();
  };

  const closeModal = () => {
    resetForm();
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
    onHide();
  };

  const renderExistingInventories = () => {
    if (!values.product || isLoadingInventories) return null;

    if (existingInventories.count === 0) return null;

    return (
      <div
        ref={inventoryListRef}
        className="mb-3"
        style={{ maxHeight: '250px', overflowY: 'auto' }}
      >
        <h6 className="fw-bold mb-1">
          Exist Inventory List (Total: {existingInventories.count})
        </h6>

        <Table size="sm">
          <thead>
            <tr className="bg-light fs-10">
              <th className="text-start">Shelf Code</th>
              <th className="text-center">Category</th>
              <th className="text-center">Expiry Date</th>
              <th className="text-end">Total Qty</th>
              <th className="text-end">Available Qty</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {existingInventories.results.map(inventory => (
              <tr
                key={inventory.id}
                className={`fs-10 ${
                  selectedProductBatch === inventory.id ? 'table-success' : ''
                }`}
                style={{ verticalAlign: 'center' }}
              >
                <td className="text-start">{inventory.shelf.code}</td>
                <td className="text-center">
                  {String(inventory.inventory_type || '').toUpperCase()}
                </td>
                <td className="text-center">{inventory.expire_date || '-'}</td>
                <td className="text-end">{inventory.quantity}</td>
                <td className="text-end">
                  {inventory.quantity - inventory.quantity_allocated}
                </td>
                <td>
                  {selectedProductBatch === inventory.id ? (
                    inventory.quantity - inventory.quantity_allocated === 0 ? (
                      <Button
                        size="sm"
                        className="btn-disabled"
                        disabled
                        style={{ minWidth: 90, textAlign: 'center' }}
                      >
                        N/A
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="btn-success"
                        style={{ minWidth: 90, textAlign: 'center' }}
                      >
                        Selected
                      </Button>
                    )
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleSelectInventory(inventory)}
                      style={{ minWidth: 90, textAlign: 'center' }}
                      disabled={
                        inventory.quantity - inventory.quantity_allocated === 0
                      } // 수량 0이면 클릭 불가
                    >
                      {inventory.quantity - inventory.quantity_allocated === 0
                        ? 'N/A'
                        : 'Select'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* 무한스크롤 sentinel: 컨테이너 바닥에 닿으면 다음 페이지 로드 */}
        <div ref={inventorySentinelRef} style={{ height: 1 }} />

        {isLoadingMoreInventories && (
          <div className="text-center py-1">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {hasLoadMoreError && !isLoadingMoreInventories && (
          <div className="text-center py-1">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={handleRetryLoadMore}
            >
              Failed to load more. Retry
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Gate Pass Item</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderExistingInventories()}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Select Product<span className="text-danger">*</span>
            </Form.Label>
            <SearchableInput
              apiService={productNameApi}
              searchField="search__icontains"
              value={values.productName}
              onChange={handleProductInputChange}
              onSelect={handleProductSelect}
              onBlur={() => handleBlur('product')}
              placeholder="Enter 2 or more characters to search products..."
              renderItem={item => (
                <div className="font-medium">{item?.name}</div>
              )}
              visibleItems={2}
              disabled={false}
            />
            {touched.product && errors.product && (
              <Form.Text className="text-danger">{errors.product}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Inventory Category<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.inventoryType}
              onChange={e => handleChange('inventoryType', e.target.value)}
              onBlur={() => handleBlur('inventoryType')}
              isInvalid={touched.inventoryType && errors.inventoryType}
            >
              <option value="">Select Inventory Category...</option>
              {INVENTORY_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.inventoryType}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Shelf<span className="text-danger">*</span>
            </Form.Label>
            <SearchableInput
              apiService={shelfApi}
              searchField="code__icontains"
              additionalParams={{ warehouse: selectedWarehouse?.id }}
              displayField="code"
              value={values.shelfCode}
              onChange={handleShelfInputChange}
              onSelect={handleShelfSelect}
              onBlur={() => handleBlur('shelf')}
              placeholder="Enter 2 or more characters to search shelf..."
              renderItem={item => (
                <div className="font-medium">{item?.code}</div>
              )}
              visibleItems={2}
              disabled={false}
            />
            {touched.shelf && errors.shelf && (
              <Form.Text className="text-danger">{errors.shelf}</Form.Text>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Quantity<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              value={values.quantity}
              onChange={e => handleChange('quantity', e.target.value)}
              placeholder="Enter Quantity..."
              onBlur={() => handleBlur('quantity')}
              isInvalid={touched.quantity && errors.quantity}
            />
            <Form.Control.Feedback type="invalid">
              {errors.quantity}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            resetForm();
            setSelectedProductBatch(null);
            setAvailableQuantity(0);
          }}
        >
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleAddItem}
          disabled={!isValid || !selectedProductBatch}
        >
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GatePassItemAddModal;
